import { DomainChange } from '~/domain/changes/DomainChange';
import { DomainStore } from '~/domain/changes/DomainStore';
import { redactDicomUids } from '~/utils/phiRedaction';

export interface PersistenceService {
    dispose(): void;
}

const REDACTED = '[redacted]';

/**
 * The change with its patient ID, names, free text and dates replaced by
 * `[redacted]`: any of them can identify a patient (a label such as "align
 * to Doe pelvis MR") or a clinician. Fields follow DICOM PS3.15 Table E.1-1.
 * There is no default case, so a new kind does not compile until classified.
 */
function maskIdentifyingFields(change: DomainChange): DomainChange {
    switch (change.kind) {
        case 'patient':
            return {
                ...change,
                snapshot: {
                    ...change.snapshot,
                    dicomPatientId: REDACTED,
                    dicomPatientName: REDACTED,
                },
            };
        case 'structureSet':
            return {
                ...change,
                snapshot: {
                    ...change.snapshot,
                    displayName: REDACTED,
                    description: REDACTED,
                    reviewerName: REDACTED,
                },
            };
        case 'roi':
            return {
                ...change,
                snapshot: { ...change.snapshot, name: REDACTED },
            };
        case 'registration':
            return {
                ...change,
                snapshot: {
                    ...change.snapshot,
                    contentLabel: REDACTED,
                    contentDescription: REDACTED,
                    contentCreatorName: REDACTED,
                    contentDate: REDACTED,
                    reviewerName: REDACTED,
                },
            };
        case 'dose':
        case 'plan':
        case 'image':
            // These snapshots hold no names, free text or dates.
            return change;
    }
}

/** The change with identifying fields and DICOM UIDs masked. */
function redactPhi(change: DomainChange): DomainChange {
    return redactDicomUids(maskIdentifyingFields(change));
}

export class LoggingPersistenceService implements PersistenceService {
    private unsubscribe: () => void;

    constructor(store: DomainStore) {
        this.unsubscribe = store.onChangeset((changes) => {
            // console.debug for production filterability; PHI redacted
            // up-front so this log line is always safe to emit.
            // eslint-disable-next-line no-console
            console.debug('[domain-persistence]', changes.map(redactPhi));
        });
    }

    dispose(): void {
        this.unsubscribe();
    }
}
